package dev.lingvo.site.components.marketing

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.ds.LCodeBlock
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.transitionColors
import dev.lingvo.site.i18n.t
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Code
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

/**
 * One entry in a documentation page.
 *
 * The contents-rail and the body are rendered from the same list, so a section can never appear in
 * one and not the other — the commonest way documentation navigation rots.
 */
class DocEntry(
    val id: String,
    val label: String,
    val children: List<DocAnchor> = emptyList(),
    val content: @Composable () -> Unit,
)

val DocsSectionStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().styleModifier {
            property("scroll-margin-top", "136px")
            property("outline", "none")
        }
    }
    // Fragment navigation focuses a reading destination, not a selected bordered panel.
    cssRule(":focus-visible > div > h2") {
        Modifier.styleModifier { property("text-decoration", "underline"); property("text-underline-offset", "5px") }
    }
    cssRule(":is(h2,h3):focus-visible") {
        Modifier.styleModifier { property("text-decoration", "underline"); property("text-underline-offset", "5px") }
    }
    Breakpoint.LG {
        Modifier.styleModifier { property("scroll-margin-top", "88px") }
    }
}

val DocsInlineCodeStyle = CssStyle.base {
    Modifier
        .backgroundColor(colorMode.toSitePalette().surfaceSunken)
        .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
        .borderRadius(Radius.Control)
        .padding(topBottom = 1.px, leftRight = Space.XS)
        .fontSize(0.85.cssRem)
        .styleModifier { property("white-space", "nowrap") }
}

/**
 * A link inside a sentence.
 *
 * Underlined as well as coloured: a link in running text that is only a hue is not a link to anyone
 * who cannot separate that hue from the paragraph around it, and the underline is the cue that
 * survives greyscale. `accent` is measured at 4.5:1 on every ground in `PaletteContrastTest`.
 */
val DocsInlineLinkStyle = CssStyle {
    base {
        Modifier
            .color(colorMode.toSitePalette().accent)
            .borderRadius(Radius.Control)
            .outline(0.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier {
                property("text-decoration", "underline")
                property("text-underline-offset", "3px")
            }
            .transitionColors()
    }
    hover {
        Modifier.color(colorMode.toSitePalette().accentHover)
    }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
}

val DocsListStyle = CssStyle.base {
    Modifier
        .margin(0.px)
        .padding(left = Space.XL)
        .color(colorMode.toSitePalette().textSecondary)
        .fontSize(TypeScale.BodySmall)
        .lineHeight(TypeScale.BodyLineHeight)
        .styleModifier {
            property("display", "grid")
            property("gap", Space.XS.toString())
        }
}

/**
 * A whole long-form page: header, titled intro, contents rail, sections, footer.
 *
 * Docs, privacy and terms are all the same shape — a lede followed by anchored sections — so they
 * share one scaffold rather than three that drift apart.
 */
@Composable
fun MarketingDocPage(
    eyebrow: String,
    title: String,
    lede: String,
    entries: List<DocEntry>,
    footnote: String? = null,
    overview: (@Composable () -> Unit)? = null,
) {
    Div(Modifier.fillMaxWidth().toAttrs()) {
        PublicHeader()
        Main {
            Div(MarketingSectionStyle.toModifier().toAttrs()) {
                Column(MarketingPageColumnStyle.toModifier()) {
                    Column(Modifier.maxWidth(46.cssRem).gap(Space.MD)) {
                        Span(
                            Modifier
                                .color(ColorMode.current.toSitePalette().textSecondary)
                                .fontSize(TypeScale.Micro)
                                .fontWeight(600)
                                .styleModifier {
                                    property("text-transform", "uppercase")
                                    property("letter-spacing", TypeScale.MicroTracking.toString())
                                }
                                .toAttrs(),
                        ) { Text(eyebrow) }
                        H1(
                            Modifier
                                .displayFont()
                                .fontSize(TypeScale.Display)
                                .fontWeight(700)
                                .lineHeight(TypeScale.DisplayLineHeight)
                                .margin(0.px)
                                .toAttrs(),
                        ) { Text(title) }
                        P(
                            Modifier
                                .color(ColorMode.current.toSitePalette().textSecondary)
                                .fontSize(TypeScale.Lead)
                                .lineHeight(TypeScale.BodyLineHeight)
                                .margin(0.px)
                                .toAttrs(),
                        ) { Text(lede) }
                    }
                    overview?.invoke()
                    DocsBody(entries)
                    if (footnote != null) {
                        P(
                            Modifier
                                .maxWidth(44.cssRem)
                                .margin(0.px)
                                .color(ColorMode.current.toSitePalette().textSecondary)
                                .fontSize(TypeScale.Small)
                                .lineHeight(TypeScale.BodyLineHeight)
                                .toAttrs(),
                        ) { Text(footnote) }
                    }
                }
            }
        }
        PublicFooter()
    }
}

/** One section inventory drives desktop, mobile and fragment navigation. */
@Composable
fun DocsBody(entries: List<DocEntry>) {
    val current = rememberDocSection(entries)
    Div(DocsLayoutStyle.toModifier().toAttrs()) {
        DocsNavigation(entries, current)
        Column(Modifier.fillMaxWidth().gap(Space.Section)) {
            entries.forEach { entry ->
                Section(DocsSectionStyle.toModifier().toAttrs {
                    id(entry.id)
                    attr("tabindex", "-1")
                    attr("data-docs-anchor", "")
                }) {
                    Column(Modifier.fillMaxWidth().gap(Space.LG)) {
                        DocsHeading(entry.label)
                        entry.content()
                    }
                }
            }
        }
    }
}

@Composable
fun DocsHeading(text: String) {
    H2(
        Modifier
            .displayFont()
            .fontSize(TypeScale.HeadingSmall)
            .fontWeight(700)
            .margin(0.px)
            .toAttrs(),
    ) { Text(text) }
}

@Composable
fun DocsSubheading(text: String, anchor: String? = null) {
    H3(
        Modifier.then(if (anchor != null) DocsSectionStyle.toModifier() else Modifier)
            .fontSize(TypeScale.Body)
            .fontWeight(600)
            .margin(top = Space.SM, bottom = 0.px)
            .toAttrs {
                if (anchor != null) {
                    id(anchor)
                    attr("tabindex", "-1")
                    attr("data-docs-anchor", "")
                }
            },
    ) { Text(text) }
}

@Composable
fun DocsProse(text: String) {
    DocsProse { Text(text) }
}

/**
 * The same paragraph, with a slot, for prose that has to carry a link.
 *
 * The whole-string form takes a `String` and renders it as text, so the docs page's single hand-off
 * to deeper documentation — "is at /docs/formats" — arrived as a path to be retyped into the
 * address bar rather than as something to follow. Reach for [DocsInlineLink] inside this.
 */
@Composable
fun DocsProse(content: @Composable () -> Unit) {
    P(
        Modifier
            .maxWidth(44.cssRem)
            .margin(0.px)
            .color(ColorMode.current.toSitePalette().textSecondary)
            .fontSize(TypeScale.BodySmall)
            .lineHeight(TypeScale.BodyLineHeight)
            .toAttrs(),
    ) { content() }
}

/** A link in running prose. Kobweb's [Link], so an in-app path routes rather than reloads. */
@Composable
fun DocsInlineLink(path: String, text: String) {
    Link(path = path, text = text, modifier = DocsInlineLinkStyle.toModifier())
}

@Composable
fun DocsList(items: List<String>) {
    Ul(DocsListStyle.toModifier().toAttrs()) {
        items.forEach { item -> Li { Text(item) } }
    }
}

/** A shell snippet or config block. */
@Composable
fun DocsCode(text: String) {
    LCodeBlock(text, label = t("marketing.docs_chrome.code", "Code"), copyable = false)
}

@Composable
fun DocsInlineCode(text: String) {
    Code(DocsInlineCodeStyle.toModifier().monoFont().toAttrs()) { Text(text) }
}
